import React, { useEffect, useMemo, useState } from 'react';
import { useApi } from '../../../hooks/useApi';
import logger from "../../../utils/logger";
import { fmtDate } from '../../../utils/ruDates';

const DEFAULT_TERM_FIELDS = [
  ['l1_rub_percent', '% L1 в кошелёк на вывод'],
  ['l1_rub_monthly_cap', 'Потолок L1 на вывод с одного приглашённого, ₽/мес (0 — без потолка)'],
  ['l1_balance_percent', '% L1 на основной баланс'],
  ['l2_balance_percent', '% L2 на основной баланс'],
  ['welcome_bonus_amount', 'Welcome-бонус приглашённому, ₽ (сразу при регистрации)'],
  ['first_purchase_bonus_percent', 'Приглашённому % к первому пополнению'],
];

// Условия реферальной ссылки обычного пользователя; партнёрский промокод их перекрывает
function ReferralDefaultsForm({ request, showStatus }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    request('/api/v1/admin/referrals/defaults')
      .then(setForm)
      .catch(error => {
        logger.error('Failed to load referral defaults', error);
        showStatus('error', 'Не удалось загрузить условия по умолчанию.');
      });
    // eslint-disable-next-line
  }, []);
  const save = async event => {
    event.preventDefault();
    setBusy(true);
    try {
      const payload = Object.fromEntries(DEFAULT_TERM_FIELDS.map(([key]) => [key, Number(form[key]) || 0]));
      const saved = await request('/api/v1/admin/referrals/defaults', {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      setForm(saved);
      showStatus('success', 'Условия по умолчанию сохранены.');
    } catch (error) {
      logger.error('Failed to save referral defaults', error);
      showStatus('error', error?.message || 'Не удалось сохранить условия.');
    } finally {
      setBusy(false);
    }
  };
  if (!form) {
    return null;
  }
  return <form onSubmit={save} style={{
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    marginBottom: '24px'
  }}>
            <h3 style={{ margin: '0 0 4px' }}>Условия по умолчанию</h3>
            <p style={{ color: '#666', margin: '0 0 16px', fontSize: '14px' }}>
                Для всех, у кого нет персонального партнёрского промокода. Условия партнёров задаются в промокоде (тип «Персональный партнёрский»).
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '12px 20px' }}>
                {DEFAULT_TERM_FIELDS.map(([key, label]) => <label key={key} style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', color: '#555' }}>
                        {label}
                        <input type="number" min="0" step="0.01" value={form[key] ?? 0} onChange={event => setForm(prev => ({
            ...prev,
            [key]: event.target.value
          }))} style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px' }} />
                    </label>)}
            </div>
            <button type="submit" disabled={busy} style={{ marginTop: '16px', padding: '8px 16px' }}>
                {busy ? 'Сохранение...' : 'Сохранить'}
            </button>
        </form>;
}

function AdminReferrals() {
  const {
    request,
    showStatus
  } = useApi();
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionBusyId, setActionBusyId] = useState('');
  // Раньше грузились все рефералы платформы одним запросом.
  const PAGE_SIZE = 200;
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  useEffect(() => {
    loadReferrals();
    // eslint-disable-next-line
  }, [page]);
  const loadReferrals = async () => {
    setLoading(true);
    try {
      const data = await request(`/api/v1/admin/referrals?skip=${page * PAGE_SIZE}&limit=${PAGE_SIZE}`);
      const rows = data || [];
      setReferrals(rows);
      setHasMore(rows.length === PAGE_SIZE);
    } catch (error) {
      logger.error('Failed to load referrals', error);
      showStatus('error', 'Не удалось загрузить рефералы.');
    } finally {
      setLoading(false);
    }
  };
  const filteredReferrals = useMemo(() => {
    if (statusFilter === 'all') {
      return referrals;
    }
    return referrals.filter(item => item.status === statusFilter);
  }, [referrals, statusFilter]);
  const stats = {
    total: referrals.length,
    active: referrals.filter(item => item.status === 'active').length,
    pending: referrals.filter(item => item.status === 'pending').length,
    totalRevenue: referrals.reduce((acc, item) => acc + (item.total_revenue || 0), 0),
    totalCommission: referrals.reduce((acc, item) => acc + (item.total_commission || 0), 0)
  };
  const updateStatus = async (inviteId, nextStatus) => {
    setActionBusyId(inviteId);
    try {
      const updated = await request(`/api/v1/admin/referrals/${inviteId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          status: nextStatus
        })
      });
      setReferrals(prev => prev.map(item => item.id === inviteId ? updated : item));
      showStatus('success', 'Статус реферала обновлен.');
    } catch (error) {
      logger.error('Failed to update referral', error);
      showStatus('error', 'Не удалось обновить статус.');
    } finally {
      setActionBusyId('');
    }
  };
  const statusColor = status => {
    switch (status) {
      case 'active':
        return '#27ae60';
      case 'pending':
        return '#f39c12';
      case 'cancelled':
        return '#e74c3c';
      default:
        return '#7f8c8d';
    }
  };
  return <div className="admin-page">
            <div style={{
      marginBottom: '30px'
    }}>
                <h1 style={{
        margin: 0
      }}>Реферальная программа</h1>
                <p style={{
        color: '#666',
        marginTop: '5px'
      }}>
                    Мониторинг приглашений, оборота и ручное управление статусами. «Cancel» останавливает начисления по приглашённому.
                </p>
            </div>

            <ReferralDefaultsForm request={request} showStatus={showStatus} />

            <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(5, 1fr)',
      gap: '20px',
      marginBottom: '24px'
    }}>
                <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
                    <div style={{
          color: '#666',
          fontSize: '14px'
        }}>Всего приглашений</div>
                    <div style={{
          fontSize: '24px',
          fontWeight: 'bold',
          marginTop: '5px'
        }}>{stats.total}</div>
                </div>
                <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
                    <div style={{
          color: '#666',
          fontSize: '14px'
        }}>Активных</div>
                    <div style={{
          fontSize: '24px',
          fontWeight: 'bold',
          marginTop: '5px',
          color: '#27ae60'
        }}>{stats.active}</div>
                </div>
                <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
                    <div style={{
          color: '#666',
          fontSize: '14px'
        }}>Ожидают</div>
                    <div style={{
          fontSize: '24px',
          fontWeight: 'bold',
          marginTop: '5px',
          color: '#f39c12'
        }}>{stats.pending}</div>
                </div>
                <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
                    <div style={{
          color: '#666',
          fontSize: '14px'
        }}>Оборот рефералов</div>
                    <div style={{
          fontSize: '24px',
          fontWeight: 'bold',
          marginTop: '5px'
        }}>{stats.totalRevenue.toLocaleString()} ₽</div>
                </div>
                <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
      }}>
                    <div style={{
          color: '#666',
          fontSize: '14px'
        }}>Выплачено комиссий</div>
                    <div style={{
          fontSize: '24px',
          fontWeight: 'bold',
          marginTop: '5px',
          color: '#3498db'
        }}>{stats.totalCommission.toLocaleString()} ₽</div>
                </div>
            </div>

            <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px'
    }}>
                <div style={{
        display: 'flex',
        gap: '8px',
        alignItems: 'center'
      }}>
                    <span>Статус:</span>
                    <select value={statusFilter} onChange={event => setStatusFilter(event.target.value)} style={{
          padding: '8px 10px',
          border: '1px solid #ddd',
          borderRadius: '6px'
        }}>
            
                        <option value="all">Все</option>
                        <option value="pending">Ожидают</option>
                        <option value="active">Активные</option>
                        <option value="cancelled">Отмененные</option>
                    </select>
                </div>
                <button type="button" onClick={loadReferrals} style={{
        padding: '8px 15px',
        background: 'white',
        border: '1px solid #ddd',
        borderRadius: '4px',
        cursor: 'pointer'
      }}>
          
                    Обновить
                </button>
            </div>

            <div style={{
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.05)',
      overflow: 'hidden'
    }}>
                <table style={{
        width: '100%',
        borderCollapse: 'collapse'
      }}>
                    <thead style={{
          background: '#fcfcfc',
          borderBottom: '2px solid #eee'
        }}>
                        <tr>
                            <th style={{
              padding: '15px',
              textAlign: 'left'
            }}>Реферер</th>
                            <th style={{
              padding: '15px',
              textAlign: 'left'
            }}>Приглашенный</th>
                            <th style={{
              padding: '15px',
              textAlign: 'center'
            }}>Статус</th>
                            <th style={{
              padding: '15px',
              textAlign: 'center'
            }}>Оборот</th>
                            <th style={{
              padding: '15px',
              textAlign: 'center'
            }}>Комиссия</th>
                            <th style={{
              padding: '15px',
              textAlign: 'center'
            }}>Действия</th>
                            <th style={{
              padding: '15px',
              textAlign: 'right'
            }}>Дата</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan="7" style={{
              padding: '40px',
              textAlign: 'center'
            }}>Загрузка...</td></tr>}
                        {!loading && filteredReferrals.length === 0 && <tr><td colSpan="7" style={{
              padding: '40px',
              textAlign: 'center'
            }}>Данных нет</td></tr>}
                        {filteredReferrals.map(ref => <tr key={ref.id} style={{
            borderBottom: '1px solid #f5f5f5'
          }}>
                                <td style={{
              padding: '15px'
            }}>
                                    <div style={{
                fontWeight: '500'
              }}>{ref.referrer_email}</div>
                                </td>
                                <td style={{
              padding: '15px'
            }}>
                                    <div style={{
                fontSize: '14px'
              }}>{ref.referred_email || '—'}</div>
                                    <div style={{
                fontSize: '12px',
                color: '#999'
              }}>{ref.referred_phone || ''}</div>
                                </td>
                                <td style={{
              padding: '15px',
              textAlign: 'center'
            }}>
                                    <span style={{
                padding: '4px 10px',
                borderRadius: '20px',
                background: `${statusColor(ref.status)}20`,
                color: statusColor(ref.status),
                fontSize: '12px',
                fontWeight: '600'
              }}>
                                        {ref.status}
                                    </span>
                                </td>
                                <td style={{
              padding: '15px',
              textAlign: 'center',
              fontWeight: '500'
            }}>
                                    {ref.total_revenue?.toLocaleString() || 0} ₽
                                </td>
                                <td style={{
              padding: '15px',
              textAlign: 'center'
            }}>
                                    <div style={{
                fontWeight: 'bold',
                color: '#27ae60'
              }}>{ref.total_commission?.toLocaleString() || 0} ₽</div>
                                </td>
                                <td style={{
              padding: '15px',
              textAlign: 'center'
            }}>
                                    <div style={{
                display: 'flex',
                gap: '6px',
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}>
                                        <button type="button" disabled={actionBusyId === ref.id || ref.status === 'pending'} onClick={() => updateStatus(ref.id, 'pending')} style={{
                  padding: '5px 8px',
                  border: '1px solid #ddd',
                  background: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}>
                    
                                            Pending
                                        </button>
                                        <button type="button" disabled={actionBusyId === ref.id || ref.status === 'active'} onClick={() => updateStatus(ref.id, 'active')} style={{
                  padding: '5px 8px',
                  border: '1px solid #27ae60',
                  background: 'white',
                  color: '#27ae60',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}>
                    
                                            Active
                                        </button>
                                        <button type="button" disabled={actionBusyId === ref.id || ref.status === 'cancelled'} onClick={() => updateStatus(ref.id, 'cancelled')} style={{
                  padding: '5px 8px',
                  border: '1px solid #e74c3c',
                  background: 'white',
                  color: '#e74c3c',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}>
                    
                                            Cancel
                                        </button>
                                    </div>
                                </td>
                                <td style={{
              padding: '15px',
              textAlign: 'right',
              color: '#666',
              fontSize: '13px'
            }}>
                                    {fmtDate(ref.created_at)}
                                </td>
                            </tr>)}
                    </tbody>
                </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 15, marginTop: 15 }}>
                <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0 || loading} style={{ padding: '8px 16px' }}>← Назад</button>
                <span style={{ color: '#666', fontSize: 14 }}>Страница {page + 1}</span>
                <button onClick={() => setPage((p) => p + 1)} disabled={!hasMore || loading} style={{ padding: '8px 16px' }}>Вперёд →</button>
            </div>
        </div>;
}
export default AdminReferrals;
